// @ts-nocheck

import { useState } from "react";
import { supabaseClient } from "@/lib/supabase/client"
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion, AnimatePresence } from "framer-motion";
import { createBrowserSupabaseClient } from "@/lib/supabase/client";
import { Avatar } from "@/components/ui/Avatar";
import { cn } from "@/lib/utils/cn";
import { useToast } from "@/components/ui/ToastProvider";
import {
  IconLoader, IconUser, IconMail, IconLock, IconBell,
  IconShield, IconEye, IconTrash, IconCheck, IconAlertCircle,
  IconKey, IconGlobe, IconMapPin
} from "@/components/ui/Icons";

const profileSchema = z.object({
  display_name: z.string().min(2, "At least 2 characters").max(50),
  username: z.string().min(3, "At least 3 characters").max(30).regex(/^[a-zA-Z0-9_.-]+$/, "Letters, numbers, _ . - only"),
  bio: z.string().max(500).optional(),
  website: z.string().url("Enter a valid URL").optional().or(z.literal("")),
  location: z.string().max(100).optional(),
});

const passwordSchema = z.object({
  new_password: z.string().min(8, "Minimum 8 characters"),
  confirm_password: z.string(),
}).refine(d => d.new_password === d.confirm_password, {
  message: "Passwords do not match", path: ["confirm_password"],
});

const deleteSchema = z.object({
  confirmation: z.string().refine(v => v === "DELETE MY ACCOUNT", {
    message: 'Type "DELETE MY ACCOUNT" exactly',
  }),
  password: z.string().min(1, "Password is required to confirm deletion"),
});

type ProfileForm = z.infer<typeof profileSchema>;
type PasswordForm = z.infer<typeof passwordSchema>;
type DeleteForm = z.infer<typeof deleteSchema>;
type Tab = "profile" | "account" | "notifications" | "privacy" | "danger";

interface SettingsClientProps {
  profile: {
    id: string; username: string; display_name: string; email: string;
    avatar_url: string | null; bio: string | null; website: string | null;
    location: string | null; settings: Record<string, unknown>;
    is_verified?: boolean; karma_score?: number;
  };
}

const tabs: { value: Tab; label: string; icon: React.FC<{ className?: string }> }[] = [
  { value: "profile", label: "Profile", icon: IconUser },
  { value: "account", label: "Account", icon: IconMail },
  { value: "notifications", label: "Notifications", icon: IconBell },
  { value: "privacy", label: "Privacy", icon: IconEye },
  { value: "danger", label: "Danger Zone", icon: IconAlertCircle },
];

export function SettingsClient({ profile }: SettingsClientProps) {
  const [activeTab, setActiveTab] = useState<Tab>("profile");
  const [saving, setSaving] = useState(false);
  const [notifSettings, setNotifSettings] = useState({
    mentions: true, replies: true, follows: true, likes: false,
    newsletter: false, digest: true,
  });
  const [privacySettings, setPrivacySettings] = useState({
    public_profile: true, show_karma: true, allow_messages: true,
    indexable: true, show_online: false,
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { success, error: showError } = useToast();
  const navigate = useNavigate();
  const supabase = supabaseClient;

  const profileForm = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      display_name: profile.display_name,
      username: profile.username,
      bio: profile.bio || "",
      website: profile.website || "",
      location: profile.location || "",
    },
  });

  const passwordForm = useForm<PasswordForm>({ resolver: zodResolver(passwordSchema) });
  const deleteForm = useForm<DeleteForm>({ resolver: zodResolver(deleteSchema) });

  const handleProfileSave = async (data: ProfileForm) => {
    setSaving(true);
    try {
      // @ts-ignore
      const { error } = await supabase.from("users").update({
        display_name: data.display_name,
        username: data.username.toLowerCase(),
        bio: data.bio || null,
        website: data.website || null,
        location: data.location || null,
      } as any).eq("id", profile.id);
      if (error) throw error;
      success("Profile saved", "Your profile has been updated.");
      // navigate.refresh() - not needed in Vite;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to save";
      showError("Save failed", msg.includes("unique") ? "That username is already taken." : msg);
    } finally { setSaving(false); }
  };

  const handlePasswordChange = async (data: PasswordForm) => {
    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: data.new_password });
      if (error) throw error;
      success("Password updated", "Your password has been changed.");
      passwordForm.reset();
    } catch (err: unknown) {
      showError("Update failed", err instanceof Error ? err.message : "Failed to update password");
    } finally { setSaving(false); }
  };

  const handleNotifSave = async () => {
    setSaving(true);
    try {
      // @ts-ignore
      await supabase.from("users").update({ settings: { ...profile.settings, notifications: notifSettings } }).eq("id", profile.id);
      success("Saved", "Notification preferences updated.");
    } catch { showError("Save failed", "Could not save preferences."); }
    finally { setSaving(false); }
  };

  const handlePrivacySave = async () => {
    setSaving(true);
    try {
      // @ts-ignore
      await supabase.from("users").update({ settings: { ...profile.settings, privacy: privacySettings } }).eq("id", profile.id);
      success("Saved", "Privacy settings updated.");
    } catch { showError("Save failed", "Could not save settings."); }
    finally { setSaving(false); }
  };

  const handleDeleteAccount = async (data: DeleteForm) => {
    setSaving(true);
    try {
      // Verify password first
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: profile.email,
        password: data.password,
      });
      if (authError) {
        deleteForm.setError("password", { message: "Incorrect password" });
        return;
      }

      // Call secure delete API route (handles anonymization + auth deletion)
      const res = await fetch("/api/users/delete", { method: "DELETE" });
      if (!res.ok) throw new Error((await res.json()).error || "Deletion failed");

      // Sign out
      await supabase.auth.signOut();
      success("Account deleted", "Your account and all data have been permanently deleted.");
      navigate("/auth/login");
    } catch (err: unknown) {
      showError("Deletion failed", err instanceof Error ? err.message : "Could not delete account. Please contact support.");
    } finally { setSaving(false); }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      {/* Tab nav */}
      <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible lg:w-48 flex-shrink-0 pb-2 lg:pb-0">
        {tabs.map(({ value, label, icon: Icon }) => (
          <button
            key={value}
            onClick={() => setActiveTab(value)}
            className={cn(
              "flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all duration-150 text-left",
              activeTab === value
                ? value === "danger"
                  ? "bg-destructive/10 text-destructive"
                  : "bg-sidebar-active text-sidebar-active-foreground"
                : "text-text-muted hover:bg-muted hover:text-foreground"
            )}
          >
            <Icon className={cn("w-4 h-4 flex-shrink-0",
              activeTab === value
                ? value === "danger" ? "text-destructive" : "text-brand"
                : "text-text-muted"
            )} />
            {label}
          </button>
        ))}
      </nav>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
          >

            {/* PROFILE TAB */}
            {activeTab === "profile" && (
              <form onSubmit={profileForm.handleSubmit(handleProfileSave)} className="space-y-6">
                <div className="card p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Public profile</h2>

                  {/* Avatar */}
                  <div className="flex items-center gap-4 mb-6 pb-6 border-b border-border-subtle">
                    <Avatar src={profile.avatar_url} alt={profile.display_name} size="xl" fallback={profile.display_name[0]} />
                    <div>
                      <p className="text-sm font-medium text-foreground">Profile photo</p>
                      <p className="text-sm text-text-muted mb-2">JPG, PNG or GIF · Max 5MB</p>
                      <button type="button" className="btn-secondary btn-sm">Change photo</button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className="label">Display name</label>
                      <div className="input-group">
                        <IconUser className="input-icon" />
                        <input {...profileForm.register("display_name")} className="input input-with-icon" placeholder="Your name" />
                      </div>
                      {profileForm.formState.errors.display_name && (
                        <p className="input-error">{profileForm.formState.errors.display_name.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="label">Username</label>
                      <div className="input-group">
                        <span className="input-prefix">@</span>
                        <input {...profileForm.register("username")} className="input input-with-prefix" placeholder="username" />
                      </div>
                      {profileForm.formState.errors.username && (
                        <p className="input-error">{profileForm.formState.errors.username.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="label">Location</label>
                      <div className="input-group">
                        <IconMapPin className="input-icon" />
                        <input {...profileForm.register("location")} className="input input-with-icon" placeholder="City, Country" />
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="label">Website</label>
                      <div className="input-group">
                        <IconGlobe className="input-icon" />
                        <input {...profileForm.register("website")} className="input input-with-icon" placeholder="https://yoursite.com" type="url" />
                      </div>
                      {profileForm.formState.errors.website && (
                        <p className="input-error">{profileForm.formState.errors.website.message}</p>
                      )}
                    </div>

                    <div className="sm:col-span-2">
                      <label className="label">Bio <span className="text-text-subtle font-normal">(optional)</span></label>
                      <textarea {...profileForm.register("bio")} rows={3}
                        className="input resize-none" placeholder="Tell the community about yourself…" />
                      <p className="text-xs text-text-subtle mt-1 text-right">
                        {profileForm.watch("bio")?.length ?? 0} / 500
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end mt-6 pt-4 border-t border-border-subtle">
                    <button type="submit" disabled={saving} className="btn-primary btn-md gap-2">
                      {saving ? <IconLoader className="w-4 h-4" /> : <IconCheck className="w-4 h-4" />}
                      Save profile
                    </button>
                  </div>
                </div>
              </form>
            )}

            {/* ACCOUNT TAB */}
            {activeTab === "account" && (
              <div className="space-y-6">
                {/* Email */}
                <div className="card p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Email address</h2>
                  <div className="input-group">
                    <IconMail className="input-icon" />
                    <input value={profile.email} disabled className="input input-with-icon opacity-60 cursor-not-allowed" />
                  </div>
                  <p className="text-xs text-text-subtle mt-2">
                    Email changes require verification. Contact support to update your email.
                  </p>
                </div>

                {/* Password */}
                <form onSubmit={passwordForm.handleSubmit(handlePasswordChange)} className="card p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-4">Change password</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="label">New password</label>
                      <div className="input-group">
                        <IconLock className="input-icon" />
                        <input {...passwordForm.register("new_password")} type="password"
                          className="input input-with-icon" placeholder="Minimum 8 characters" />
                      </div>
                      {passwordForm.formState.errors.new_password && (
                        <p className="input-error">{passwordForm.formState.errors.new_password.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="label">Confirm new password</label>
                      <div className="input-group">
                        <IconKey className="input-icon" />
                        <input {...passwordForm.register("confirm_password")} type="password"
                          className="input input-with-icon" placeholder="Repeat new password" />
                      </div>
                      {passwordForm.formState.errors.confirm_password && (
                        <p className="input-error">{passwordForm.formState.errors.confirm_password.message}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end mt-6 pt-4 border-t border-border-subtle">
                    <button type="submit" disabled={saving} className="btn-primary btn-md gap-2">
                      {saving ? <IconLoader className="w-4 h-4" /> : <IconLock className="w-4 h-4" />}
                      Update password
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* NOTIFICATIONS TAB */}
            {activeTab === "notifications" && (
              <div className="card p-6">
                <h2 className="text-lg font-semibold text-foreground mb-6">Notification preferences</h2>
                <div className="space-y-5">
                  {[
                    { key: "mentions", label: "Mentions", desc: "When someone @mentions you in a post or comment" },
                    { key: "replies", label: "Replies", desc: "When someone replies to your posts or comments" },
                    { key: "follows", label: "New followers", desc: "When someone follows you" },
                    { key: "likes", label: "Likes & upvotes", desc: "When your content receives likes" },
                    { key: "digest", label: "Weekly digest", desc: "A weekly summary of top content in your topics" },
                    { key: "newsletter", label: "Newsletter", desc: "Product updates and news from Squids" },
                  ].map(({ key, label, desc }) => (
                    <label key={key} className="flex items-start gap-4 cursor-pointer group">
                      <div className="relative mt-0.5">
                        <input type="checkbox"
                          checked={notifSettings[key as keyof typeof notifSettings]}
                          onChange={(e) => setNotifSettings(prev => ({ ...prev, [key]: e.target.checked }))}
                          className="sr-only peer" />
                        <div className="w-10 h-6 bg-muted rounded-full transition-colors peer-checked:bg-brand" />
                        <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{label}</p>
                        <p className="text-xs text-text-muted">{desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="flex justify-end mt-6 pt-4 border-t border-border-subtle">
                  <button onClick={handleNotifSave} disabled={saving} className="btn-primary btn-md gap-2">
                    {saving ? <IconLoader className="w-4 h-4" /> : <IconCheck className="w-4 h-4" />}
                    Save preferences
                  </button>
                </div>
              </div>
            )}

            {/* PRIVACY TAB */}
            {activeTab === "privacy" && (
              <div className="card p-6">
                <h2 className="text-lg font-semibold text-foreground mb-6">Privacy settings</h2>
                <div className="space-y-5">
                  {[
                    { key: "public_profile", label: "Public profile", desc: "Allow anyone to view your profile and posts" },
                    { key: "show_karma", label: "Show karma score", desc: "Display your karma score on your public profile" },
                    { key: "allow_messages", label: "Allow direct messages", desc: "Let other users send you direct messages" },
                    { key: "indexable", label: "Search indexable", desc: "Allow search engines to index your profile" },
                    { key: "show_online", label: "Show online status", desc: "Let others see when you're active" },
                  ].map(({ key, label, desc }) => (
                    <label key={key} className="flex items-start gap-4 cursor-pointer">
                      <div className="relative mt-0.5">
                        <input type="checkbox"
                          checked={privacySettings[key as keyof typeof privacySettings]}
                          onChange={(e) => setPrivacySettings(prev => ({ ...prev, [key]: e.target.checked }))}
                          className="sr-only peer" />
                        <div className="w-10 h-6 bg-muted rounded-full transition-colors peer-checked:bg-brand" />
                        <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{label}</p>
                        <p className="text-xs text-text-muted">{desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="flex justify-end mt-6 pt-4 border-t border-border-subtle">
                  <button onClick={handlePrivacySave} disabled={saving} className="btn-primary btn-md gap-2">
                    {saving ? <IconLoader className="w-4 h-4" /> : <IconCheck className="w-4 h-4" />}
                    Save settings
                  </button>
                </div>
              </div>
            )}

            {/* DANGER ZONE TAB */}
            {activeTab === "danger" && (
              <div className="space-y-6">
                <div className="card border-destructive/30 p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center flex-shrink-0">
                      <IconAlertCircle className="w-5 h-5 text-destructive" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-foreground">Delete account</h2>
                      <p className="text-sm text-text-muted mt-0.5">
                        Permanently delete your account, all posts, comments, and data. This cannot be undone.
                      </p>
                    </div>
                  </div>

                  {!showDeleteConfirm ? (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="btn-destructive btn-md gap-2"
                    >
                      <IconTrash className="w-4 h-4" />
                      Delete my account
                    </button>
                  ) : (
                    <AnimatePresence>
                      <motion.form
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        onSubmit={deleteForm.handleSubmit(handleDeleteAccount)}
                        className="mt-4 p-4 bg-destructive/5 rounded-xl border border-destructive/20 space-y-4"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <IconAlertCircle className="w-4 h-4 text-destructive flex-shrink-0" />
                          <p className="text-sm font-medium text-destructive">This action is permanent and irreversible.</p>
                        </div>

                        <div>
                          <label className="label text-foreground">
                            Type <span className="font-mono text-destructive font-semibold">DELETE MY ACCOUNT</span> to confirm
                          </label>
                          <input
                            {...deleteForm.register("confirmation")}
                            className="input font-mono"
                            placeholder="DELETE MY ACCOUNT"
                            autoComplete="off"
                          />
                          {deleteForm.formState.errors.confirmation && (
                            <p className="input-error">{deleteForm.formState.errors.confirmation.message}</p>
                          )}
                        </div>

                        <div>
                          <label className="label text-foreground">Your password</label>
                          <div className="input-group">
                            <IconLock className="input-icon" />
                            <input
                              {...deleteForm.register("password")}
                              type="password"
                              className="input input-with-icon"
                              placeholder="Enter your password"
                              autoComplete="current-password"
                            />
                          </div>
                          {deleteForm.formState.errors.password && (
                            <p className="input-error">{deleteForm.formState.errors.password.message}</p>
                          )}
                        </div>

                        <div className="flex gap-3 justify-end pt-2">
                          <button type="button" onClick={() => { setShowDeleteConfirm(false); deleteForm.reset(); }}
                            className="btn-secondary btn-md">
                            Cancel
                          </button>
                          <button type="submit" disabled={saving} className="btn-destructive btn-md gap-2">
                            {saving ? <IconLoader className="w-4 h-4" /> : <IconTrash className="w-4 h-4" />}
                            Permanently delete
                          </button>
                        </div>
                      </motion.form>
                    </AnimatePresence>
                  )}
                </div>

                {/* Export data */}
                <div className="card p-6">
                  <h3 className="text-base font-semibold text-foreground mb-1">Export your data</h3>
                  <p className="text-sm text-text-muted mb-4">Download a copy of all your posts, comments, and profile data.</p>
                  <button
                    onClick={async () => {
                      const { data } = await supabase.from("users").select("*").eq("id", profile.id).single();
                      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a"); a.href = url; a.download = "squids-data.json"; a.click();
                      URL.revokeObjectURL(url);
                      success("Export ready", "Your data has been downloaded.");
                    }}
                    className="btn-secondary btn-md"
                  >
                    Export data
                  </button>
                </div>
              </div>
            )}

          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
